import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SegmentedControl from "@/components/SegmentedControl";
import Logo from "@/components/Logo";
import { statusColors } from "@/lib/mock-data";
import { SAMPLE_REPORTS } from "@/lib/sample-reports";

type Range = "7d" | "30d" | "90d" | "all";
const RANGE_DAYS: Record<Range, number> = { "7d": 7, "30d": 30, "90d": 90, all: Infinity };
const STATUSES = ["all", "complete", "in-review", "submitted", "draft"];
const TYPES = ["all", ...Array.from(new Set(SAMPLE_REPORTS.map((r) => r.type)))];

export default function PublicReports() {
  const [q, setQ] = useState("");
  const [range, setRange] = useState<Range>("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("newest");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return SAMPLE_REPORTS.filter((r) =>
      r.daysAgo <= RANGE_DAYS[range] &&
      (status === "all" || r.status === status) &&
      (type === "all" || r.type === type) &&
      (!term || `${r.title} ${r.city} ${r.state} ${r.type}`.toLowerCase().includes(term)),
    ).sort((a, b) => sort === "title" ? a.title.localeCompare(b.title) : sort === "oldest" ? b.daysAgo - a.daysAgo : a.daysAgo - b.daysAgo);
  }, [q, range, status, type, sort]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 h-16">
          <Link to="/" aria-label="Home"><Logo /></Link>
          <div className="flex gap-2"><Button asChild size="sm" variant="outline"><Link to="/demo">Live demo</Link></Button><Button asChild size="sm"><Link to="/auth">Sign in</Link></Button></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Sample Reports</h1>
          <p className="text-muted-foreground mt-1">Browse example environmental reports. These are demonstrations — no client data.</p>
        </div>

        <div role="group" aria-label="Report filters" className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports" aria-label="Search reports" className="pl-9 h-9" />
          </div>
          <SegmentedControl ariaLabel="Time range" value={range} onChange={(v) => setRange(v as Range)}
            options={[{ value: "7d", label: "7d" }, { value: "30d", label: "30d" }, { value: "90d", label: "90d" }, { value: "all", label: "All" }]} />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Filter by status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 w-[170px]" aria-label="Filter by report type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All types" : t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-[140px]" aria-label="Sort reports"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="title">Title A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="text-sm text-muted-foreground" aria-live="polite">{rows.length} report{rows.length === 1 ? "" : "s"}</p>

        {rows.length === 0 ? (
          <p className="text-center text-muted-foreground py-16">No reports match these filters.</p>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <li key={r.id} className="glass-card rounded-xl p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-semibold leading-tight">{r.title}</h2>
                  <Badge className={`${statusColors[r.status]} capitalize shrink-0`}>{r.status}</Badge>
                </div>
                <p className="text-xs font-medium text-primary">{r.type}</p>
                <p className="text-sm text-muted-foreground">{r.summary}</p>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" aria-hidden />{r.city}, {r.state}</span>
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" aria-hidden />{r.daysAgo}d ago</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="text-center pt-4">
          <Button asChild><Link to="/auth">Create your own report</Link></Button>
        </div>
      </main>
    </div>
  );
}
